import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Divider, Grid, IconButton, InputBase, Paper, Typography,} from '@mui/material';
import {
    useDeleteMutation,
    useGetAllQuery,
    useGetSingleQuery,
    useUpsertMutation,
} from '../../services/rtkQueryServices';
import {DataGrid, GridColumns, GridRowParams} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LoadingIndicator from '../Loading';
import FormContainer, {FormEnum} from './FormContainer';
import {v4} from 'uuid';
import ErrorBoundary from '../ErrorBoundary';
import {renderToString} from 'react-dom/server';
import QRCode from 'react-qr-code';
import {utils, writeFileXLSX} from 'xlsx';


interface CrudPageI {
    pageTitle: string;
    singularName: string;
    resource:
        | 'sessions'
        | 'attendees'
        | 'companies'
        | 'speakers'
        | 'messages'
        | 'faq'
        | 'contact-us'
        | 'pages'
        | 'change-types'
        | 'notifications'
        | 'venue'
        | 'organizer';
    allowApprove?: boolean;
    allowAdd: boolean;
    allowEdit: boolean;
    allowDelete: boolean;
    allowReject?: boolean;
    newPayload: any;
    validateFn?: any;
    ReadOnlyComponent: React.FC<{ data: any }>;
    EditFormComponent: React.FC<{ data: any; setPayload: any }>;
    tableColumns?: GridColumns<any>;
    allowPrint?: boolean
}

export default function GenericCrudPage({
                                            pageTitle,
                                            singularName,
                                            resource,
                                            allowApprove,
                                            allowAdd,
                                            allowEdit,
                                            allowDelete,
                                            allowReject,
                                            newPayload,
                                            validateFn,
                                            ReadOnlyComponent,
                                            EditFormComponent,
                                            tableColumns,
                                            allowPrint = false
                                        }: CrudPageI) {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [search, setSearch] = useState('');
    const [mode, setMode] = useState<FormEnum | undefined>();
    const [tempSearch, setTempSearch] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [size, setSize] = useState(100);
    const {data, isLoading, isError, isSuccess, isFetching, refetch} =
        useGetAllQuery({resource, page, search, pageSize});

    const {
        data: selectedData,
        isLoading: isSelectedLoading,
        isFetching: isSelectedFetching,
    } = useGetSingleQuery({resource, id: selectedId});
    const [upsertApi, {isLoading: upsertLoading, isSuccess: upsertSuccess}] =
        useUpsertMutation();
    const [deleteApi, {isLoading: deleteLoading, isSuccess: deleteSuccess}] =
        useDeleteMutation();

    const {refetch: reFetchAll, data: response, isSuccess: isAllSuccess,isLoading:isAllLoading} =
        useGetAllQuery({resource, page: 0, search, pageSize: size});

    useEffect(() => {
        setPage(0);
        refetch();
        setSize(data?.total??100)
    }, []);

    const formKey = useMemo(() => v4(), [data, selectedData]);

    useEffect(() => {
        if (selectedData && (document as any).querySelector('#form-component')) {
            (document as any).querySelector('#form-component').scrollIntoView({
                behavior: 'smooth',
            });
        }
    }, [formKey]);

    const generateUserCard = (user: { name: any; title: any; uuid: any; }) => {
        return (
            <div className="user-card" style={{pageBreakBefore: 'always'}}>
                <h2 style={{fontWeight: 'bold', textAlign: 'center'}}>{user.name}</h2>
                <p style={{textAlign: 'center', fontSize: '20px'}}>{user.title}</p>
                <QRCode value={user.uuid} size={125} style={{display: 'block', margin: '0 auto'}}/>
            </div>
        );
    };

    const printAllUserCards = async () => {
        setSize(data?.total??100);
        await reFetchAll();
        if (isAllSuccess) {
            const userCards = response?.result?.map((user: {
                name: any;
                title: any;
                uuid: any;
            }) => generateUserCard(user));
            const userCardHtml = userCards.map((userCard: any) => renderToString(userCard)).join('');

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            // Set the page size to A5 using CSS
            const printStyles = `
            <style>
              @page {
                size: 10.5cm 14.8cm;
              }
              body {
                margin: 0;
              }
              @top-center {
                content: "top-center";
              }
              @bottom-center {
                content: "bottom-center";
              }
            </style>
        `;

            // Write the user card HTML and styles to the iframe
            iframe?.contentDocument?.open();
            iframe?.contentDocument?.write(`
            <html lang="en">
              <head>
                ${printStyles}
              </head>
              <body>
                ${userCardHtml}
              </body>
            </html>
        `);
            iframe?.contentDocument?.close();

            // Print the content within the iframe
            iframe?.contentWindow?.print();

            // Remove the iframe after printing is done
            iframe?.parentNode?.removeChild(iframe);
        }
    };

    const exportToExcel = useCallback(async () => {
        setSize(data?.total??100);
        await reFetchAll();

        if (isAllSuccess) {
            interface MyObject {
                name: string;
                title: string;
                email: string;
                // Add more fields and their types as needed
            }

            const selectedFields: Array<keyof MyObject> = ['name', 'title', 'email'];
            const filteredData = response.result.map((item: { [x: string]: string | undefined; }) => {
                // Create a new object with only the selected fields
                const selectedData: Partial<MyObject> = {};
                for (const field of selectedFields) {
                    if (item[field] !== undefined) {
                        selectedData[field] = item[field];
                    }
                }
                return selectedData as MyObject; // Cast back to MyObject type
            });

            const ws = utils.json_to_sheet(filteredData);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, "Data");
            writeFileXLSX(wb, "SheetJSReactAoO.xlsx");
        }
    }, [response]);

    return (
        <>
            <LoadingIndicator loading={isLoading}/>
            <LoadingIndicator loading={isAllLoading}/>
            <Grid id='crud-title' container spacing={2} mb={2}>
                <Grid item xs={12} lg={5}>
                    <Grid item>
                        <Typography variant='h4' mb={1}>
                            {pageTitle}
                        </Typography>
                        <Divider/>
                        {/* <Alert severity='error'> some message</Alert>
              <Alert severity='success'> some message</Alert> */}
                    </Grid>
                    <Grid item mt={2} sx={{display: 'flex', gap: 2}}>
                        <ErrorBoundary>
                            <Paper
                                component='form'
                                sx={{display: 'flex', flex: 1}}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    setSearch(tempSearch);
                                    setPage(0);
                                    setSelectedId('');
                                    setMode(undefined);
                                }}
                            >
                                <InputBase
                                    placeholder='Search...'
                                    sx={{flex: 1, paddingLeft: 1}}
                                    onChange={(e) => {
                                        setTempSearch(e.target.value);
                                    }}
                                    value={tempSearch}
                                />
                                <IconButton
                                    type='button'
                                    onClick={() => {
                                        if (search) {
                                            setTempSearch('');
                                            setSearch('');
                                        }
                                        setMode(undefined);
                                        setSelectedId('');
                                    }}
                                >
                                    {!search ? <SearchIcon/> : <CloseIcon/>}
                                </IconButton>
                            </Paper>
                        </ErrorBoundary>
                        {allowAdd ? (
                            <Button
                                variant='contained'
                                color='success'
                                onClick={() => {
                                    setMode(FormEnum.add);
                                    setSelectedId('');
                                }}
                            >
                                ADD
                            </Button>
                        ) : (
                            <></>
                        )}
                        {allowPrint ? (
                            <Button
                                variant='contained'
                                color='success'
                                onClick={() => {
                                    setTimeout(()=>{
                                        printAllUserCards();
                                    },2000);
                                }}
                            >
                                Print All
                            </Button>
                        ) : (
                            <></>
                        )}
                        {allowPrint ? (
                            <Button
                                variant='contained'
                                color='success'
                                onClick={() => {
                                    if (isAllLoading){
                                    }
                                    setTimeout(()=>{
                                        exportToExcel();
                                    },2000);
                                }}
                            >
                                Export All
                            </Button>
                        ) : (
                            <></>
                        )}


                    </Grid>
                    <ErrorBoundary>
                        <Grid item style={{height: '630px'}} mt={2}>
                            {isSuccess && isSuccess && !isLoading && !isError && (
                                <DataGrid
                                    sx={{backgroundColor: 'background.paper'}}
                                    columns={
                                        tableColumns || [
                                            {
                                                field: 'name',
                                                headerName: 'Name',
                                                width: 200,
                                            },
                                            {
                                                field: 'title',
                                                headerName: 'Title',
                                                width: 200,
                                            },
                                        ]
                                    }
                                    rowHeight={40}
                                    rows={data.result || []}
                                    pageSize={pageSize}
                                    page={data.page}
                                    paginationMode={'server'}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    onPageSizeChange={(size: number) => {
                                        setPage(0);
                                        setPageSize(size);
                                    }}
                                    rowCount={data.total}
                                    loading={isLoading}
                                    error={isError}
                                    onPageChange={(page) => setPage(page)}
                                    onRowClick={(params: GridRowParams<any>) => {
                                        setSelectedId('' + params.id);
                                        setMode(FormEnum.edit);
                                    }}
                                />
                            )}
                        </Grid>
                    </ErrorBoundary>
                </Grid>
                <ErrorBoundary>
                    <Grid item xs={12} lg={7} sx={{height: '100%'}}>
                        {/* {addSuccess && <Alert color='success'>Add success</Alert>}
            {editSuccess && <Alert color='success'>Edit success</Alert>}
            {deleteSuccess && <Alert color='success'>Delete success</Alert>} */}
                        {((selectedData && selectedId) || mode === FormEnum.add) && (
                            <FormContainer
                                singularName={singularName}
                                key={formKey}
                                newPayload={newPayload}
                                data={mode === FormEnum.add ? null : selectedData}
                                mode={mode}
                                validate={validateFn}
                                onCancel={() => {
                                    setSelectedId('');
                                    setMode(undefined);
                                }}
                                onDelete={async (id: string) => {
                                    const resp: any = deleteApi({resource, id});
                                    if (!resp.error) {
                                        setSelectedId('');
                                        setMode(undefined);
                                    }
                                }}
                                onAdd={async (data: any) => {
                                    const resp: any = await upsertApi({
                                        resource,
                                        body: {
                                            ...data,
                                            ...(!data.uuid ? {uuid: v4()} : {}),
                                        },
                                        id: '',
                                    });
                                    if (!resp.error) {
                                        setSelectedId('');
                                        setMode(undefined);
                                    }
                                }}
                                onEdit={async (data: any) => {
                                    const resp: any = await upsertApi({
                                        resource,
                                        body: data,
                                        id: data._id,
                                    });
                                    if (!resp.error) {
                                        setSelectedId('');
                                        setMode(undefined);
                                    }
                                }}
                                onApprove={async (data: any) => {
                                    debugger;
                                    const resp: any = await upsertApi({
                                        resource,
                                        body: data,
                                        id: data._id + '/approve',
                                    });
                                    if (!resp.error) {
                                        setSelectedId('');
                                        setMode(undefined);
                                    }
                                }}
                                onReject={async (id: any) => {
                                    debugger;
                                    const resp: any = await upsertApi({
                                        resource,
                                        body: {},
                                        id: id + '/reject',
                                    });
                                    if (!resp.error) {
                                        setSelectedId('');
                                        setMode(undefined);
                                    }
                                }}
                                approveAllowed={allowApprove}
                                addAllowed={allowAdd}
                                editAllowed={allowEdit}
                                deleteAllowed={allowDelete}
                                rejectAllowed={allowReject}
                                ReadOnlyComponent={ReadOnlyComponent}
                                EditFormComponent={EditFormComponent}
                            />
                        )}
                    </Grid>
                </ErrorBoundary>
            </Grid>
        </>
    );
}
